import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useEffect, useState } from "react";
import { Button, Card, Table } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import api from "../../config/api";
import {
  getDate,
  LocalFormatDate,
  parseAmount,
  rupeeFormat,
} from "../../config/functions";
import ReportGrandTotalBar from "./ReportGrandTotalBar";
import {
  ClientRegistrationType,
  fetchClientRegistrationAction,
} from "../../redux/actions/clientRegistration";
import { AppDispatch, StoreState } from "../../redux/store";
import GstSalesReportFilter from "./GstSalesReportFilter";

interface InvoiceData {
  financialYear?: string;
  gstRate: number;
  taxableValue: number;
  igst: number;
  cgst: number;
  sgst: number;
  gstValue: number;
  totalAmount: number;
}

const GstPurchaseReturnHsnReport = () => {
  const dispatch = useDispatch<AppDispatch>();

  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [branchId, setBranchId] = useState<string>("");
  const [customerType, setCustomerType] = useState<string>("");
  const [tableData, setTableData] = useState<InvoiceData[]>([]);
  const [triggerSearch, setTriggerSearch] = useState<boolean>(false);
  const [hasSearched, setHasSearched] = useState(false);

  const clients = useSelector<StoreState, ClientRegistrationType[]>(
    (state) => state.clientRegistration,
  );

  useEffect(() => {
    dispatch(fetchClientRegistrationAction());
  }, []);

  useEffect(() => {
    if (triggerSearch) {
      api()
        .get(
          `reports/gstHsnPurchaseReturnWise/?fromDate=${getDate(fromDate)}&toDate=${getDate(
            toDate,
          )}&branchId=${branchId}&customerType=${customerType}`,
        )
        .then((res) => {
          if (res.status === 200 && res.data.data) {
            setTableData(res.data.data);
          }
        })
        .catch((error) => {
          console.error("Error fetching purchase HSN data:", error);
        });

      setTriggerSearch(false);
    }
  }, [triggerSearch, fromDate, toDate, branchId, customerType]);

  const [filters, setFilters] = useState<any>({});

  const handleSearch = (newFilters: any) => {
    setFilters(newFilters);
    setFromDate(newFilters.fromDate);
    setToDate(newFilters.toDate);
    setBranchId(newFilters.branchId);
    setCustomerType(newFilters.customerType);
    setHasSearched(true);
    setTriggerSearch(true);
  };

  const totalTaxableValue = (tableData || []).reduce(
    (sum, r) => sum + (Number(r.taxableValue) || 0),
    0,
  );
  const totalIgst = (tableData || []).reduce(
    (sum, r) => sum + (Number(r.igst) || 0),
    0,
  );
  const totalCgst = (tableData || []).reduce(
    (sum, r) => sum + (Number(r.cgst) || 0),
    0,
  );
  const totalSgst = (tableData || []).reduce(
    (sum, r) => sum + (Number(r.sgst) || 0),
    0,
  );
  const totalGstValue = (tableData || []).reduce(
    (sum, r) => sum + (Number(r.gstValue) || 0),
    0,
  );
  const totalAmount = (tableData || []).reduce(
    (sum, r) => sum + parseAmount(r.totalAmount),
    0,
  );

  const toRow = (item: InvoiceData, index: number) => [
    String(index + 1),
    String(item.gstRate ?? ""),
    rupeeFormat(item.taxableValue) ?? "0",
    rupeeFormat(item.igst) ?? "0",
    rupeeFormat(item.cgst) ?? "0",
    rupeeFormat(item.sgst) ?? "0",
    rupeeFormat(item.gstValue) ?? "0",
    rupeeFormat(item.totalAmount) ?? "0",
  ];

  const downloadCSV = () => {
    const headerLabels = [
      "SI.No",
      "GST Rate",
      "Taxable Value",
      "IGST",
      "CGST",
      "SGST",
      "GST Value",
      "Total Amount",
    ];

    const rows = tableData?.map((item, idx) => toRow(item, idx)) || [];

    rows.push([
      "", // SI.No
      "", // Rate
      rupeeFormat(totalTaxableValue) ?? "0", // Taxable Value
      rupeeFormat(totalIgst) ?? "0", // IGST
      rupeeFormat(totalCgst) ?? "0", // CGST
      rupeeFormat(totalSgst) ?? "0", // SGST
      rupeeFormat(totalGstValue) ?? "0", // GST Value
      rupeeFormat(totalAmount) ?? "0", // Total Amount
    ]);

    const csvCell = (value: unknown) => {
      const s = value === null || value === undefined ? "" : String(value);
      return `"${s.replace(/"/g, '""')}"`;
    };

    const csvContent = [
      headerLabels.map(csvCell).join(","),
      ...rows.map((row) => row.map(csvCell).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `TAP_purchase_return_hsn_${customerType}_${LocalFormatDate(getDate(filters?.fromDate)) || ""}_${LocalFormatDate(getDate(filters?.toDate)) || ""}`;
    link.click();
  };

  const downloadPDF = () => {
    const logoUrl = clients[0]?.shopPhoto;

    const doc = new jsPDF("landscape");
    const marginX = 10;
    const boxWidth = doc.internal.pageSize.width - 2 * marginX;
    const boxHeight = 25;
    let startY = 15;

    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");

    doc.rect(marginX, startY - 5, boxWidth, boxHeight);

    if (logoUrl) {
      doc.addImage(logoUrl, "JPEG", marginX + 5, startY - 2, 25, 20);
    }

    const paddingBetweenHeaderAndTable = 3;
    const textX = marginX + 35;

    doc.text(clients[0]?.shopShortName || "", textX, startY);
    doc.setFont("helvetica", "normal");
    doc.text(
      `${clients[0]?.address1 || ""}, ${clients[0]?.address2 || ""}, ${
        clients[0]?.district || ""
      }`,
      textX,
      startY + 5,
    );
    doc.text(
      `${clients[0]?.state || ""} - ${clients[0]?.pincode || ""}, ${
        clients[0]?.contactNo1 || ""
      }`,
      textX,
      startY + 10,
    );
    doc.text(`GSTIN: ${clients[0]?.gstNo || ""}`, textX, startY + 15);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    const heading = `GST ${customerType} Purchase Hsn report`;
    const headingWidth = doc.getTextWidth(heading);
    const pageWidth = doc.internal.pageSize.width;
    doc.text(heading, marginX + boxWidth - headingWidth - 10, startY + 8);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    const financialYearText = `Financial Year: ${
      tableData[0]?.financialYear || ""
    }`;
    const headingStartX = marginX + boxWidth - headingWidth - 10;
    doc.text(financialYearText, headingStartX, startY + 13);

    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    const fromDate =
      "From : " + (LocalFormatDate(getDate(filters?.fromDate)) || "");
    const toDate = "To : " + (LocalFormatDate(getDate(filters?.toDate)) || "");

    const tableStartY = startY + boxHeight + paddingBetweenHeaderAndTable;
    doc.text(fromDate, marginX, tableStartY);
    doc.text(toDate, marginX + 50, tableStartY);

    autoTable(doc, {
      startY: 47,
      showFoot: "lastPage",
      head: [
        [
          "SI.No",
          "GST Rate",
          "Taxable Value",
          "IGST",
          "CGST",
          "SGST",
          "GST Value",
          "Total Amount",
        ],
      ],
      body: (tableData || []).map((item, idx) => toRow(item, idx)),
      foot: [
        [
          "",
          "",
          rupeeFormat(totalTaxableValue) ?? "0",
          rupeeFormat(totalIgst) ?? "0",
          rupeeFormat(totalCgst) ?? "0",
          rupeeFormat(totalSgst) ?? "0",
          rupeeFormat(totalGstValue) ?? "0",
          rupeeFormat(totalAmount) ?? "0",
        ],
      ],
      theme: "grid",
      styles: { fontSize: 8, textColor: 0, lineColor: 0, lineWidth: 0.1 },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: 0,
        fontStyle: "bold",
        lineColor: 0,
        lineWidth: 0.1,
      },
      footStyles: {
        fillColor: [255, 255, 255],
        halign: "right",
      },
      margin: { top: 25, left: 10, right: 10 },
      columnStyles: {
        0: { halign: "center" }, // SI.No
        1: { halign: "right" }, // GST Rate
        2: { halign: "right" }, // Taxable Value
        3: { halign: "right" }, // IGST
        4: { halign: "right" }, // CGST
        5: { halign: "right" }, // SGST
        6: { halign: "right" }, // GST Value
        7: { halign: "right" }, // Total Amount
      },
    });

    // Footer Section (Page Number)
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      let pageHeight = doc.internal.pageSize.height;
      let footerY = pageHeight - 11;

      doc.setFontSize(10);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth - 30, footerY);
    }

    doc.save(
      `TAP_purchase_return_hsn_${customerType}_${LocalFormatDate(getDate(filters?.fromDate)) || ""}_${LocalFormatDate(getDate(filters?.toDate)) || ""}.pdf`,
    );
  };
  // tableData.map((item, index) => {
  //     console.log(item.quantity);
  // });

  return (
    <>
      <GstSalesReportFilter onSearch={handleSearch} showCustomerType={false} />
      <ReportGrandTotalBar show={hasSearched} amount={totalAmount} />
      {hasSearched && tableData.length > 0 ? (
        <Card className="mt-2 p-2 shadow-lg border-0 rounded-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="fw-bold">Purchase HSN details</h5>
            <div>
              <Button
                className="me-2 btn-sm"
                variant="success"
                onClick={downloadCSV}
              >
                Download CSV
              </Button>
              <Button
                className="me-2 btn-sm"
                variant="success"
                onClick={downloadPDF}
              >
                Download PDF
              </Button>
            </div>
          </div>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>SI.No</th>
                <th>GST Rate</th>
                <th>Taxable Value</th>
                <th>IGST</th>
                <th>CGST</th>
                <th>SGST</th>
                <th>GST Value</th>
                <th>Total Amount</th>
              </tr>
            </thead>

            <tbody>
              {tableData?.map((item, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>{item.gstRate}</td>
                  <td>{rupeeFormat(item.taxableValue)}</td>
                  <td>{rupeeFormat(item.igst)}</td>
                  <td>{rupeeFormat(item.cgst)}</td>
                  <td>{rupeeFormat(item.sgst)}</td>
                  <td>{rupeeFormat(item.gstValue)}</td>
                  <td>{rupeeFormat(item.totalAmount)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      ) : hasSearched ? (
        <h4 className="text-center fw-bold text-secondary">
          No such data found!
        </h4>
      ) : null}
    </>
  );
};

export default GstPurchaseReturnHsnReport;
